import { Check, ChevronDown } from "lucide-react";
import {
  DAY_KEYS,
  DAY_LABELS,
  DAY_LABELS_SHORT,
  type DayKey,
  todayDayKeyInRange,
} from "@/lib/days";
import type {
  RecurringTasksConfig,
  RecurringTasksDayData,
  RecurringTasksWeekData,
  WeeklyRecurringTasksConfig,
} from "@/lib/schema";
import type { WidgetDayViewProps } from "@/widgets/types";
import { useAutoCollapse } from "@/widgets/useAutoCollapse";
import {
  awardDay,
  earliestCheckableFrom,
  isCheckableOn,
  scoreDay,
  tasksForDay,
  weeklyTasksForDay,
} from "./scoring";

function emptyHint(config: RecurringTasksConfig): string {
  if (config.tasks.length === 0) return "Ingen oppgaver lagt til.";
  if (config.reset === "weekly") {
    const earliest = earliestCheckableFrom(config);
    if (earliest === null) return "Ingen oppgaver lagt til.";
    return `Oppgavene kan krysses av fra ${DAY_LABELS[earliest].toLowerCase()}.`;
  }
  const union = new Set<DayKey>();
  for (const t of config.tasks) for (const d of t.visibleDays) union.add(d);
  if (union.size === 0) return "Ingen dager valgt for oppgavene.";
  const ordered = DAY_KEYS.filter((k) => union.has(k)).map(
    (k) => DAY_LABELS_SHORT[k],
  );
  const list =
    ordered.length === 1
      ? ordered[0]
      : `${ordered.slice(0, -1).join(", ")} og ${ordered[ordered.length - 1]}`;
  return `Oppgaver kun synlig på ${list}.`;
}

/**
 * The award day (CONTEXT.md) for a set finishing right now: the wall-clock
 * day, clamped to Sunday when that falls outside this week. Clamping here
 * rather than at read time is what makes the stored value exact — it records
 * when the points were awarded, not when the work was done.
 */
function awardDayForNow(startDate: string | undefined): DayKey {
  if (!startDate) return "sun";
  return todayDayKeyInRange(startDate, new Date()) ?? "sun";
}

interface Row {
  id: string;
  label: string;
  checked: boolean;
  /** Weekly reset only: the box has not opened yet on this day. */
  locked: boolean;
  /** The day it opens, shown on a locked row. */
  opensOn: DayKey | null;
}

type Props = WidgetDayViewProps<RecurringTasksConfig>;

export function DayView({
  config,
  data,
  weekData,
  dayKey,
  onChangeDay,
  onChangeWeek,
  readOnly = false,
  week,
}: Props) {
  const dayData = (data ?? { checked: {} }) as RecurringTasksDayData;
  const weekRuntime = (weekData ?? { checked: {} }) as RecurringTasksWeekData;
  const isWeekly = config.reset === "weekly";

  const rows: Row[] = isWeekly
    ? weeklyTasksForDay(config, dayKey).map((t) => {
        const open = isCheckableOn(t, dayKey);
        return {
          id: t.id,
          label: t.label,
          checked: weekRuntime.checked[t.id] === true,
          locked: !open,
          opensOn: open ? null : t.checkableFrom,
        };
      })
    : tasksForDay(config, dayKey).map((t) => ({
        id: t.id,
        label: t.label,
        checked: dayData.checked[t.id] === true,
        locked: false,
        opensOn: null,
      }));

  const award = isWeekly
    ? awardDay(config as WeeklyRecurringTasksConfig, weekRuntime)
    : null;
  // Weekly reset shows the week's award, not this day's score — deliberately
  // not what this card contributes to today's tile. The day in brackets is
  // what reconciles the two (CONTEXT.md: "Award day").
  const shownScore = isWeekly
    ? award !== null
      ? config.points
      : 0
    : scoreDay(config, dayData, dayKey, weekRuntime);

  const toggleTask = (taskId: string) => {
    if (readOnly) return;
    if (isWeekly) {
      const checked = {
        ...weekRuntime.checked,
        [taskId]: !weekRuntime.checked[taskId],
      };
      // Any toggle from a finished set unfinishes it, and any toggle into a
      // finished set mints a fresh award — so the stamp is always rewritten
      // from the state we are about to store, never left stale.
      const finished =
        config.tasks.length > 0 &&
        config.tasks.every((t) => checked[t.id] === true);
      const next: RecurringTasksWeekData = {
        checked,
        awardedOn: finished ? awardDayForNow(week?.startDate) : null,
      };
      onChangeWeek(next);
    } else {
      const next: RecurringTasksDayData = {
        checked: { ...dayData.checked, [taskId]: !dayData.checked[taskId] },
      };
      onChangeDay(next);
    }
  };

  // Ferdiglogget (CONTEXT.md): every task open to this day is checked —
  // under weekly reset that excludes rows whose day has not come round, so
  // the card can be finished for today long before any points are awarded.
  const actionable = rows.filter((r) => !r.locked);
  const setSize = rows.length;
  const checkedCount = rows.filter((r) => r.checked).length;
  const loggingCompleted =
    !readOnly && actionable.length > 0 && actionable.every((r) => r.checked);

  const { open, toggle } = useAutoCollapse(dayKey, loggingCompleted);
  const collapsible = !readOnly && rows.length > 0;
  const collapsed = collapsible && !open;

  if (rows.length === 0 && !readOnly) return null;

  return (
    <section
      className={`widget-card${readOnly ? " read-only" : ""}${collapsed ? " collapsed" : ""}${loggingCompleted ? " logging-completed" : ""}`}
      data-testid={`widget-day-${config.id}`}
      data-logging-completed={loggingCompleted ? "true" : "false"}
    >
      <header className="widget-card-head">
        <h3 className="widget-card-title">{config.label}</h3>
        <div className="widget-card-head-right">
          <span
            className="widget-card-score"
            data-testid={`widget-score-${config.id}`}
          >
            <b>{shownScore}</b>
            <span className="max"> / {config.points}p</span>
            {award !== null ? (
              <span
                className="award-day"
                data-testid={`widget-award-day-${config.id}`}
              >
                {" "}
                ({DAY_LABELS[award].toLowerCase()})
              </span>
            ) : null}
          </span>
          {collapsible ? (
            <button
              type="button"
              className="widget-card-toggle"
              onClick={toggle}
              aria-label={collapsed ? "Utvid" : "Skjul"}
              aria-expanded={!collapsed}
              data-testid={`widget-collapse-${config.id}`}
            >
              <ChevronDown className="chev" size={16} />
            </button>
          ) : null}
        </div>
      </header>
      {collapsed ? (
        <div
          className="widget-card-summary"
          data-testid={`widget-summary-${config.id}`}
        >
          <span className="summary-line">
            {checkedCount}/{setSize} oppgaver
          </span>
        </div>
      ) : null}
      {rows.length === 0 ? (
        <p className="hint" data-testid={`widget-visibility-hint-${config.id}`}>
          {emptyHint(config)}
        </p>
      ) : (
        <div className="widget-card-body">
          <div className="widget-card-submeta">
            {checkedCount}/{setSize} · alle = {config.points}p
          </div>
          <div className="checklist">
            {rows.map((r) => {
              const interactive = !readOnly && !r.locked;
              return (
                // biome-ignore lint/a11y/useSemanticElements: custom-styled checkbox with icon + label; native input would require restructuring.
                <div
                  key={r.id}
                  className={`cl-item${r.checked ? " done" : ""}${r.locked ? " locked" : ""}`}
                  role="checkbox"
                  aria-checked={r.checked}
                  aria-disabled={!interactive}
                  tabIndex={interactive ? 0 : -1}
                  onClick={interactive ? () => toggleTask(r.id) : undefined}
                  onKeyDown={
                    interactive
                      ? (e) => {
                          if (e.key === " " || e.key === "Enter") {
                            e.preventDefault();
                            toggleTask(r.id);
                          }
                        }
                      : undefined
                  }
                  data-testid={`task-checkbox-${r.id}`}
                  data-locked={r.locked ? "true" : "false"}
                >
                  <span
                    className={`check${r.checked ? " on" : ""}`}
                    aria-hidden="true"
                  >
                    <Check size={14} />
                  </span>
                  <span className="cl-text">{r.label || "(uten navn)"}</span>
                  {r.opensOn !== null ? (
                    <span
                      className="cl-locked-day"
                      data-testid={`task-opens-on-${r.id}`}
                    >
                      ({DAY_LABELS[r.opensOn].toLowerCase()})
                    </span>
                  ) : null}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </section>
  );
}
